"use client";

import React, { useEffect, useState } from 'react';
import { Library, Plus, FileText, Download, Search, Filter, Edit2, Trash2, Save, X, ExternalLink, Upload, Eye } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';

interface Resource {
  id: string;
  title: string;
  description?: string;
  category: string;
  url?: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  is_public: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
}

const Resources = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const categories = ['AI', 'GNI', 'Training', 'SEO', 'Marketing'];

  useEffect(() => {
    fetchResources();
    
    // Set up real-time subscription
    const subscription = supabase
      .channel('resources-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'resources' },
        () => fetchResources()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterResources();
  }, [resources, searchTerm, categoryFilter]);

  const fetchResources = async () => {
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (!error && data) setResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(resource => 
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(resource => resource.category === categoryFilter);
    }

    setFilteredResources(filtered);
  };

  const handleSaveResource = async (resource: Resource) => {
    try {
      const resourceData = {
        title: resource.title,
        description: resource.description,
        category: resource.category,
        url: resource.url,
        file_path: resource.file_path,
        file_type: resource.file_type,
        file_size: resource.file_size,
        is_public: resource.is_public
      };

      if (editingResource?.id) {
        // Update existing resource
        const { error } = await supabase
          .from('resources')
          .update(resourceData)
          .eq('id', resource.id);

        if (error) throw error;
      } else {
        // Create new resource
        const { error } = await supabase
          .from('resources')
          .insert(resourceData);

        if (error) throw error;
      }

      setEditingResource(null);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving resource:', error);
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este recurso?')) return;
    
    try {
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', resourceId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('resources')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('resources')
        .getPublicUrl(fileName);

      setEditingResource(editingResource ? {
        ...editingResource,
        file_path: data.path,
        file_type: fileExt,
        file_size: file.size,
        url: publicUrl
      } : {
        id: '',
        title: file.name,
        file_path: data.path,
        file_type: fileExt,
        file_size: file.size,
        url: publicUrl,
        category: 'Training',
        is_public: false,
        created_at: '',
        updated_at: ''
      });
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownload = async (resource: Resource) => {
    try {
      // Increment download count
      await supabase
        .from('resources')
        .update({ download_count: (resource.download_count || 0) + 1 })
        .eq('id', resource.id);

      // Download file or open URL
      if (resource.url) {
        window.open(resource.url, '_blank');
      } else if (resource.file_path) {
        const { data } = supabase.storage
          .from('resources')
          .getPublicUrl(resource.file_path);
        
        window.open(data.publicUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading resource:', error);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'AI': 'bg-purple-50 text-purple-600',
      'GNI': 'bg-blue-50 text-blue-600',
      'Training': 'bg-green-50 text-green-600',
      'SEO': 'bg-orange-50 text-orange-600',
      'Marketing': 'bg-pink-50 text-pink-600'
    };
    return colors[category] || 'bg-gray-50 text-gray-600';
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return <FileText size={24} className="text-gray-400" />;
    
    const iconMap: { [key: string]: JSX.Element } = {
      'pdf': <FileText size={24} className="text-red-500" />,
      'doc': <FileText size={24} className="text-blue-500" />,
      'docx': <FileText size={24} className="text-blue-500" />,
      'video': <FileText size={24} className="text-purple-500" />,
      'link': <ExternalLink size={24} className="text-green-500" />
    };
    
    return iconMap[fileType.toLowerCase()] || <FileText size={24} className="text-gray-400" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredResourcesList = filteredResources;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-charcoal">Recursos</h1>
          <p className="text-charcoal-muted">Biblioteca de documentos y guías técnicas.</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <Plus size={20} /> Subir Recurso
        </button>
      </div>

      <div className="bg-white rounded-4xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50 flex gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar recursos..." 
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="all">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 p-6 gap-6">
          {filteredResourcesList.length > 0 ? filteredResourcesList.map((resource) => (
            <div key={resource.id} className="group p-6 bg-gray-50 rounded-3xl border border-transparent hover:border-primary/20 hover:bg-white hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                  {getFileIcon(resource.file_type)}
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => handleDownload(resource)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Download size={16} className="text-gray-400" />
                  </button>
                  <button 
                    onClick={() => setEditingResource(resource)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit2 size={16} className="text-gray-400" />
                  </button>
                  <button 
                    onClick={() => handleDeleteResource(resource.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
              
              <h3 className="font-bold text-charcoal mb-2 line-clamp-2">{resource.title}</h3>
              {resource.description && (
                <p className="text-sm text-charcoal-muted mb-4 line-clamp-3">{resource.description}</p>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${getCategoryColor(resource.category)}`}>
                  {resource.category}
                </span>
                <div className="flex items-center gap-3 text-xs text-charcoal-muted">
                  <span>{formatFileSize(resource.file_size)}</span>
                  <span>{resource.download_count || 0} descargas</span>
                  <span className={`px-2 py-1 rounded-full ${
                    resource.is_public ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'
                  }`}>
                    {resource.is_public ? 'Público' : 'Privado'}
                  </span>
                </div>
              </div>
            </div>
          )) : (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-charcoal-muted">
              <Library size={48} className="mb-4 opacity-20" />
              <p>No se encontraron recursos</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Resource Modal */}
      {(showAddModal || editingResource) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-charcoal">
                {editingResource ? 'Editar Recurso' : 'Nuevo Recurso'}
              </h2>
              <button 
                onClick={() => {
                  setEditingResource(null);
                  setShowAddModal(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Título</label>
                <input
                  type="text"
                  value={editingResource?.title || ''}
                  onChange={(e) => setEditingResource(editingResource ? {...editingResource, title: e.target.value} : {
                    id: '',
                    title: e.target.value,
                    category: 'Training',
                    is_public: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Descripción</label>
                <textarea
                  value={editingResource?.description || ''}
                  onChange={(e) => setEditingResource(editingResource ? {...editingResource, description: e.target.value} : {
                    id: '',
                    title: '',
                    description: e.target.value,
                    category: 'Training',
                    is_public: false,
                    created_at: '',
                    updated_at: ''
                  })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Categoría</label>
                  <select
                    value={editingResource?.category || 'Training'}
                    onChange={(e) => setEditingResource(editingResource ? {...editingResource, category: e.target.value} : {
                      id: '',
                      title: '',
                      category: e.target.value,
                      is_public: false,
                      created_at: '',
                      updated_at: ''
                    })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">URL (opcional)</label>
                  <input
                    type="url"
                    value={editingResource?.url || ''}
                    onChange={(e) => setEditingResource(editingResource ? {...editingResource, url: e.target.value} : {
                      id: '',
                      title: '',
                      url: e.target.value,
                      category: 'Training',
                      is_public: false,
                      created_at: '',
                      updated_at: ''
                    })}
                    placeholder="https://..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Archivo</label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <Upload size={20} />
                    <span>{editingResource?.file_path ? 'Cambiar archivo' : 'Seleccionar archivo'}</span>
                  </label>
                  {uploadingFile && (
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      Subiendo...
                    </div>
                  )}
                  {editingResource?.file_path && (
                    <span className="text-sm text-charcoal-muted">
                      {editingResource.file_type?.toUpperCase()} ({formatFileSize(editingResource.file_size)})
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={editingResource?.is_public || false}
                  onChange={(e) => setEditingResource(editingResource ? {...editingResource, is_public: e.target.checked} : {
                    id: '',
                    title: '',
                    category: 'Training',
                    is_public: e.target.checked,
                    created_at: '',
                    updated_at: ''
                  })}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <label htmlFor="is_public" className="text-sm font-medium text-charcoal">
                  Recurso público
                </label>
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => {
                  setEditingResource(null);
                  setShowAddModal(false);
                }}
                className="flex-1 px-6 py-3 border border-gray-200 text-charcoal rounded-2xl font-bold hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => editingResource && handleSaveResource(editingResource)}
                disabled={uploadingFile}
                className="flex-1 btn-primary disabled:opacity-50"
              >
                <Save size={20} /> {editingResource ? 'Guardar Cambios' : 'Crear Recurso'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources;